import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import { LocalStorageKeys } from '@apps/main/new-desktop/core/models/localStorage';
import type { PreviouslySeenHotels } from '@apps/main/new-desktop/core/models/previouslySeenUserSeession';

import { LocalStorageService } from '@libs/utils';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

export const usePreviouslyUserSessionStore = defineStore(
  'PreviouslyUserSessionStore',
  () => {
    const localStorageService = new LocalStorageService();

    // State
    const previouslySeenHotels = ref<PreviouslySeenHotels>(
      localStorageService.readItem<PreviouslySeenHotels>(
        LocalStorageKeys.PreviouslySeenHotels
      ) || {}
    );

    // Getters
    const previouslySeenHotelCodes = computed(() =>
      Object.keys(previouslySeenHotels.value)
    );

    // Actions
    function loadPreviouslySeenHotels(): void {
      previouslySeenHotels.value =
        localStorageService.readItem<PreviouslySeenHotels>(
          LocalStorageKeys.PreviouslySeenHotels
        ) || {};
    }

    function updatePreviouslySeenHotels(hotelCode: string): void {
      const today = new SemboDate().formatUTC(DateFormat.ISO);
      previouslySeenHotels.value[hotelCode] = today;
      localStorageService.writeItem(
        LocalStorageKeys.PreviouslySeenHotels,
        previouslySeenHotels.value
      );
    }

    function cleanOldHotels(): void {
      const DAYS_IN_TWO_WEEKS = 14;
      const twoWeeksAgo = new SemboDate();
      twoWeeksAgo.sub({ days: DAYS_IN_TWO_WEEKS });

      previouslySeenHotels.value = Object.fromEntries(
        Object.entries(previouslySeenHotels.value).filter(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ([_, lastSeenDate]) =>
            new SemboDate(lastSeenDate).formatUTC(DateFormat.ISO) >=
            twoWeeksAgo.formatUTC(DateFormat.ISO)
        )
      );

      localStorageService.writeItem(
        LocalStorageKeys.PreviouslySeenHotels,
        previouslySeenHotels.value
      );
    }

    return {
      // State
      previouslySeenHotels,

      // Actions
      loadPreviouslySeenHotels,
      updatePreviouslySeenHotels,
      cleanOldHotels,

      // Getters
      previouslySeenHotelCodes,
    };
  }
);
